import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, ArrowRight, Sparkles, BookOpen, Loader2, MessageSquare, Lightbulb, BrainCircuit } from "lucide-react";
import QuestionFeedback from "@/components/QuestionFeedback";
import CoachToolModal from "@/components/CoachToolModal";

const VARIANTS = [
  { id: "content", label: "Content", icon: BookOpen, blurb: "Discrete recall — definitions, terminology, straight-forward understanding." },
  { id: "application", label: "Application", icon: Sparkles, blurb: "Passage-style scenarios that require applying the concept to a novel situation." },
];

export default function QuestionBank() {
  const [sp] = useSearchParams();
  const topicId = sp.get("topic");
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [topics, setTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chosenTopic, setChosenTopic] = useState(topicId || "");
  const [variant, setVariant] = useState("content");
  const [stats, setStats] = useState({ content: 0, application: 0 });
  const [generating, setGenerating] = useState(false);
  const [aiTool, setAiTool] = useState(null); // { type, defaults }

  useEffect(() => {
    Promise.all([
      api.get("/topics", { params: { limit: 3000 } }),
      api.get("/subjects"),
    ]).then(([tRes, sRes]) => {
      setTopics(tRes.data);
      setSubjects(sRes.data);
    });
  }, []);

  const loadQuestions = useCallback(
    async (tid, v) => {
      if (!tid) return;
      const [qRes, sRes] = await Promise.all([
        api.get("/questions", { params: { topic_id: tid, variant: v, limit: 50 } }),
        api.get("/questions/stats", { params: { topic_id: tid } }),
      ]);
      setQuestions(qRes.data);
      setStats(sRes.data);
      setIdx(0);
      setSelected(null);
      setShowResult(false);
      setScore({ correct: 0, total: 0 });
    },
    [],
  );

  useEffect(() => {
    if (chosenTopic) loadQuestions(chosenTopic, variant);
  }, [chosenTopic, variant, loadQuestions]);

  // Treat a question as "missing" if it lacks the required MCQ shape.
  // Guards against transient shape mismatches during hot-reload / API race
  // conditions (rare but produces a TypeError from `q.choices.map(...)`).
  const rawQ = questions[idx];
  const q =
    rawQ && Array.isArray(rawQ.choices) && rawQ.choices.length > 0
      ? rawQ
      : null;

  const submit = async () => {
    if (selected == null || !q) return;
    const correct = selected === q.correct;
    setShowResult(true);
    setScore((s) => ({ correct: s.correct + (correct ? 1 : 0), total: s.total + 1 }));
    try {
      await api.post("/attempts", { topic_id: chosenTopic, question_id: q.id, selected, correct });
    } catch (e) {
      // non-blocking
    }
  };

  const next = () => {
    setSelected(null);
    setShowResult(false);
    setIdx(idx + 1);
  };

  const generateMore = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const r = await api.post(`/topics/${chosenTopic}/generate-questions`, null, {
        params: { variant, count: 5 },
      });
      if (r.data.generated > 0) {
        toast.success(`Generated ${r.data.generated} new ${variant} questions.`);
        await loadQuestions(chosenTopic, variant);
        window.dispatchEvent(new Event("pm-billing-refresh"));
      } else {
        toast.error("No questions produced — try again in a moment.");
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail?.error === "out_of_quota") {
        toast.error(detail.reason || "Out of credits.", {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/app/plans") },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Generation failed. Try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  if (!chosenTopic) {
    // Group topics by subject, prioritize curated (analogy present) first.
    const bySubject = {};
    topics.forEach((t) => {
      const sid = t.subject_id || "other";
      if (!bySubject[sid]) bySubject[sid] = [];
      bySubject[sid].push(t);
    });
    Object.values(bySubject).forEach((list) =>
      list.sort((a, b) => {
        const aHas = a.analogy ? 0 : 1;
        const bHas = b.analogy ? 0 : 1;
        if (aHas !== bHas) return aHas - bHas;
        return a.name.localeCompare(b.name);
      }),
    );
    return (
      <div data-testid="qbank-select-page">
        <h1 className="font-display text-4xl font-bold mb-3">Question Bank</h1>
        <p className="text-lg mb-8" style={{ color: "var(--text-secondary)" }}>
          Choose a topic → then flip between <b>Content</b> (recall) and <b>Application</b> (passage-style) practice.
        </p>
        <div className="space-y-8">
          {subjects.map((s) => {
            const list = bySubject[s.id] || [];
            if (!list.length) return null;
            return (
              <section key={s.id} data-testid={`qbank-subject-${s.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{s.emoji}</span>
                  <h2 className="font-display text-xl font-bold">{s.name}</h2>
                  <span className="pm-badge" style={{ background: "var(--muted)", color: "var(--text-muted)" }}>
                    {list.length}
                  </span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.slice(0, 30).map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setChosenTopic(t.id)}
                      className="pm-card pm-card-hover p-3 text-left"
                      data-testid={`qbank-topic-${t.id}`}
                    >
                      <div className="font-medium text-sm">{t.name}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
                        {t.analogy ? "✦ curated" : t.category}
                      </div>
                    </button>
                  ))}
                </div>
                {list.length > 30 && (
                  <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                    Showing 30 of {list.length} — use the search bar to jump to more.
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    );
  }

  const topicName = topics.find((t) => t.id === chosenTopic)?.name || "Topic";

  return (
    <div className="max-w-3xl mx-auto" data-testid="qbank-page">
      <div className="mb-6">
        <Link
          to="/app/questions"
          onClick={(e) => { e.preventDefault(); setChosenTopic(""); }}
          className="text-sm underline"
          style={{ color: "var(--primary)" }}
          data-testid="qbank-back-to-topics"
        >
          ← All topics
        </Link>
        <h1 className="font-display text-3xl font-bold mt-2" data-testid="qbank-topic-title">{topicName}</h1>
      </div>

      {/* Variant tabs */}
      <div
        className="flex items-center gap-2 mb-6 p-1 rounded-full w-fit"
        style={{ background: "var(--muted)" }}
        data-testid="qbank-variant-tabs"
        role="tablist"
      >
        {VARIANTS.map((v) => {
          const active = variant === v.id;
          const count = stats[v.id] || 0;
          const Icon = v.icon;
          return (
            <button
              key={v.id}
              onClick={() => setVariant(v.id)}
              role="tab"
              aria-selected={active}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
              style={{
                background: active ? "var(--primary)" : "transparent",
                color: active ? "white" : "var(--text-secondary)",
              }}
              data-testid={`qbank-variant-${v.id}`}
            >
              <Icon size={14} />
              {v.label}
              <span
                className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{
                  background: active ? "rgba(255,255,255,0.22)" : "var(--paper)",
                  color: active ? "white" : "var(--text-muted)",
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }} data-testid="qbank-variant-blurb">
        {VARIANTS.find((v) => v.id === variant)?.blurb}
      </p>

      {/* Question card or empty state */}
      {!q ? (
        <div className="pm-card p-8 text-center" data-testid={questions.length === 0 ? "qbank-empty" : "qbank-done"}>
          {questions.length === 0 ? (
            <>
              <div className="text-4xl mb-3">🌱</div>
              <h2 className="font-display text-2xl font-bold mb-2">No {variant} questions yet</h2>
              <p className="mb-6" style={{ color: "var(--text-secondary)" }}>
                Generate a starter pack to begin practicing this topic.
              </p>
            </>
          ) : (
            <>
              <div className="text-4xl mb-3">🎉</div>
              <h2 className="font-display text-2xl font-bold mb-2">All done!</h2>
              <p className="mb-6" style={{ color: "var(--text-secondary)" }}>
                You got {score.correct} of {score.total} correct ({score.total ? Math.round((score.correct / score.total) * 100) : 0}%).
              </p>
            </>
          )}
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={generateMore}
              disabled={generating}
              className="pm-btn-primary inline-flex items-center gap-2"
              data-testid="qbank-generate-more-empty-btn"
            >
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Generate 5 questions · 0.5 credit
            </button>
            <Link to="/app/progress" className="pm-btn-secondary" data-testid="qbank-view-progress">
              See progress
            </Link>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 text-sm" style={{ color: "var(--text-muted)" }}>
            <span>Question {idx + 1} of {questions.length}</span>
            <span
              className="pm-badge"
              style={{
                background: q.difficulty === "hard" ? "#fdecea" : q.difficulty === "medium" ? "var(--hl-yellow)" : "var(--hl-green)",
                color: "var(--text)",
              }}
            >
              {q.difficulty}
            </span>
          </div>

          <div className="pm-card p-8 mb-6">
            <h2 className="font-display text-2xl font-bold mb-6 leading-snug" data-testid="question-text">
              {q.question}
            </h2>
            <div className="space-y-3">
              {q.choices.map((c, i) => {
                const isCorrect = showResult && i === q.correct;
                const isWrong = showResult && i === selected && i !== q.correct;
                return (
                  <button
                    key={i}
                    onClick={() => !showResult && setSelected(i)}
                    disabled={showResult}
                    className="w-full text-left p-4 rounded-xl border-2 transition-colors flex items-center gap-3"
                    style={{
                      borderColor: isCorrect ? "var(--mastery)" : isWrong ? "var(--weakness)" : selected === i ? "var(--primary)" : "var(--border)",
                      background: isCorrect ? "var(--hl-green)" : isWrong ? "#fdecea" : selected === i ? "var(--muted)" : "var(--paper)",
                    }}
                    data-testid={`choice-${i}`}
                  >
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: "var(--muted)" }}>
                      {["A", "B", "C", "D"][i]}
                    </div>
                    <span className="flex-1">{c}</span>
                    {isCorrect && <Check size={18} style={{ color: "var(--mastery)" }} />}
                    {isWrong && <X size={18} style={{ color: "var(--weakness)" }} />}
                  </button>
                );
              })}
            </div>

            {showResult && (
              <div className="mt-6 p-4 rounded-xl" style={{ background: "var(--muted)" }} data-testid="explanation">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="pm-badge" style={{ background: "var(--primary)", color: "white" }}>Explanation</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      onClick={() => setAiTool({
                        type: "explain",
                        defaults: {
                          question: q.question,
                          student_answer: selected != null ? q.choices[selected] : "",
                          correct_answer: q.choices[q.correct],
                          subject: q.subject_id,
                          topic_id: chosenTopic,
                        },
                      })}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                      style={{ background: "var(--paper)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}
                      data-testid="ai-explain-btn"
                      title="Deep AI explanation — Pro (0.25 credit)"
                    >
                      <Lightbulb size={12} /> AI Explain
                    </button>
                    <button
                      onClick={() => setAiTool({
                        type: "step",
                        defaults: {
                          problem: q.question,
                          subject: q.subject_id,
                        },
                      })}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                      style={{ background: "var(--paper)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}
                      data-testid="ai-step-btn"
                      title="Step-by-step solution — Pro (0.25 credit)"
                    >
                      <BrainCircuit size={12} /> Step-by-step
                    </button>
                    <button
                      onClick={() => {
                        const prefill = `Can you help me understand this MCAT question?\n\nQ: ${q.question}\n\nA: ${q.choices[q.correct]}\n\nExplanation: ${q.explanation}\n\nWhy is that the right answer, and how would I recognize this pattern next time?`;
                        window.dispatchEvent(new CustomEvent("pm-open-coach", {
                          detail: { prefill, topicId: chosenTopic, topicName },
                        }));
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                      style={{ background: "var(--paper)", color: "var(--primary)", border: "1.5px solid var(--primary)" }}
                      data-testid="ask-coach-btn"
                      title="Chat with the Coach about this (0.25 credit)"
                    >
                      <MessageSquare size={12} /> Chat with coach
                    </button>
                  </div>
                </div>
                <p className="text-sm leading-relaxed">{q.explanation}</p>
                <QuestionFeedback questionId={q.id} aiGenerated={q.ai_generated} />
              </div>
            )}
          </div>

          <div className="flex justify-between items-center flex-wrap gap-3">
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>Score: {score.correct}/{score.total}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={generateMore}
                disabled={generating}
                className="pm-btn-secondary inline-flex items-center gap-2 text-sm"
                data-testid="qbank-generate-more-btn"
                title="Generate 5 more questions of this variant (0.5 credit)"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Generate more · 0.5 credit
              </button>
              {!showResult ? (
                <button onClick={submit} disabled={selected == null} className="pm-btn-primary" data-testid="submit-answer-btn">
                  Submit
                </button>
              ) : (
                <button onClick={next} className="pm-btn-primary inline-flex items-center gap-2" data-testid="next-question-btn">
                  Next <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {aiTool && (
        <CoachToolModal
          open={!!aiTool}
          onClose={() => setAiTool(null)}
          type={aiTool.type}
          defaults={aiTool.defaults}
        />
      )}
    </div>
  );
}
